import { CONSTANTS } from '@/utils/constants';
import { formatDateRangeForUrl } from '@/components/common/ActionPlan/utils/du-an-utils';
import { getOrganizationIndexedDB } from '@/utils/organizationIndexedDBUtils';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { ProjectNode } from '@/types/action-plan/DepartmentActionPlanView';
import type { OrgDepartment, OrgEmployee } from '@/types/organizationIndexedDB';

export const TREE_SELECT_DEPARTMENT_SYNCED_EVENT = 'treeSelectDepartmentSynced';

export type SetTreeSelectDepartmentFn = (
  data: {
    selectedValue?: { value: string; label: string };
    tenantID?: string;
    idSelect?: string | null;
    isEmployee?: boolean;
    parentID?: string | null;
    isCompany?: boolean;
  },
  isActionPlan?: boolean
) => void;

const EMP_DEP_VALUE_RE = /^emp_(.+)_dep_(.+)$/;

function resolveEmployeeFromOrg(
  orgData: Awaited<ReturnType<typeof getOrganizationIndexedDB>>,
  empId: string
): OrgEmployee | null {
  if (!orgData?.employees?.length || !empId) return null;
  const keys = new Set<string>([
    empId,
    empId.endsWith('_nv') ? empId : `${empId}_nv`,
    empId.replace(/_nv$/i, ''),
  ]);
  for (const key of keys) {
    const found = orgData.employees.find((e) => e.ID === key);
    if (found) return found;
  }
  return null;
}

function applyTreeSelectPayload(
  payload: {
    selectedValue: { value: string; label: string };
    tenantID: string;
    idSelect: string;
    isEmployee: boolean;
    parentID: string | null;
    isCompany: boolean;
  },
  setTreeSelectDepartment: SetTreeSelectDepartmentFn,
  isSetTreeSelectDepartment: boolean
): boolean {
  setTreeSelectDepartment(payload, isSetTreeSelectDepartment);

  const store = usePrivateStore.getState();
  store.setDepartmentId(payload.idSelect);
  store.setAppHeader({
    selectedDepartment: payload.selectedValue,
    departmentId: payload.idSelect,
    departmentLabel: payload.selectedValue.label,
    tenantID: payload.tenantID,
  });
  store.initRequestHeader();

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(TREE_SELECT_DEPARTMENT_SYNCED_EVENT, { detail: payload }));
  }

  return true;
}

export type NavigateCongViecDuAnFromDepartmentParams = {
  record: { id: string; originalData?: unknown };
  departmentId: string;
  idSelect: string;
  /** Tab URL: pt | dg | ht (khớp tabMap trong ActionPlanTreeTable) */
  tab: string;
  dateRange: string | null | undefined;
  profileId?: string | null;
  tenantId?: string | null;
  setTreeSelectDepartment: SetTreeSelectDepartmentFn;
  isSetTreeSelectDepartment?: boolean;
  hienThiMode?: number;
  /** Optional URL prefix (e.g. '/van-hoa'). */
  routePrefix?: string;
};

export type TreeSelectPayload = Parameters<typeof applyTreeSelectPayload>[0];

/** BP/NV trên cây ứng với idBoPhan (`emp_*_dep_*` / id BP / id NV) — chỉ tính, không ghi store. */
export async function resolveTreeSelectPayloadFromIdBoPhan(
  idBoPhan: string,
  tenantId: string | null | undefined
): Promise<TreeSelectPayload | null> {
  const id = String(idBoPhan || '').trim();
  if (!id || id === '0') return null;

  const empDepMatch = id.match(EMP_DEP_VALUE_RE);
  if (empDepMatch) {
    const [, rawEmpId, deptId] = empDepMatch;
    const orgData = await getOrganizationIndexedDB();
    const emp = resolveEmployeeFromOrg(orgData, rawEmpId);
    const dept =
      orgData?.departments?.find((d) => d.ID === deptId) ?? null;
    const resolvedEmpId = String(emp?.ID ?? rawEmpId);
    const resolvedDeptId = String(dept?.ID ?? deptId);

    if (emp?.IsQuanLy && dept) {
      return {
        selectedValue: { value: resolvedDeptId, label: dept.Ten || '' },
        tenantID: tenantId || '',
        idSelect: resolvedDeptId,
        isEmployee: false,
        parentID: dept.ParentID ?? null,
        isCompany: dept.Key === 1,
      };
    }

    const treeValue = `emp_${resolvedEmpId}_dep_${resolvedDeptId}`;
    const label = String(emp?.TenNhanVien ?? resolvedEmpId);

    return {
      selectedValue: { value: treeValue, label },
      tenantID: tenantId || '',
      idSelect: resolvedEmpId,
      isEmployee: true,
      parentID: dept?.ParentID ?? resolvedDeptId ?? null,
      isCompany: dept?.Key === 1,
    };
  }

  const orgData = await getOrganizationIndexedDB();
  let select: OrgDepartment | OrgEmployee | null = null;
  let isEmployee = false;

  const resolveAsEmployee = (empId: string) => {
    const empGet = resolveEmployeeFromOrg(orgData, empId);
    if (!empGet) return;
    if (empGet.IsQuanLy) {
      isEmployee = false;
      select = orgData?.departments?.find((d) => d.ID === empGet.BPID) ?? null;
    } else {
      isEmployee = true;
      select = empGet;
    }
  };

  if (id.includes('_nv')) {
    resolveAsEmployee(id);
  } else {
    select = orgData?.departments?.find((d) => d.ID === id) ?? null;
    isEmployee = false;
    // NV thường: URL chỉ có ID thuần (không _nv / emp_*_dep_*)
    if (!select) {
      resolveAsEmployee(id);
    }
  }
  if (!select) return null;

  let idSelect = select.ID;
  let parentID: string | null = 'ParentID' in select ? select.ParentID ?? null : null;
  let isCompany = false;
  let selectedValue: { value: string; label: string };
  if (isEmployee) {
    const emp = select as unknown as OrgEmployee;
    const dept = orgData?.departments?.find((d) => d.ID === emp.BPID);
    const deptId = dept?.ID ?? emp.BPID ?? '';
    parentID = dept?.ParentID ?? deptId;
    isCompany = dept?.Key === 1;
    idSelect = emp.ID;
    selectedValue = {
      value: `emp_${emp.ID}_dep_${deptId}`,
      label: emp.TenNhanVien || '',
    };
  } else {
    const dept = select as OrgDepartment;
    isCompany = dept.Key === 1;
    selectedValue = { value: dept.ID, label: dept.Ten || '' };
    idSelect = dept.ID;
    parentID = dept.ParentID ?? null;
  }

  return {
    selectedValue,
    tenantID: tenantId || '',
    idSelect,
    isEmployee,
    parentID,
    isCompany,
  };
}

/** Đồng bộ cây bộ phận từ idBoPhan URL (ưu tiên khi khác idSelect trong store). */
export async function syncTreeSelectDepartmentFromIdBoPhan(
  idBoPhan: string,
  tenantId: string | null | undefined,
  setTreeSelectDepartment: SetTreeSelectDepartmentFn,
  isSetTreeSelectDepartment = true
): Promise<boolean> {
  const payload = await resolveTreeSelectPayloadFromIdBoPhan(idBoPhan, tenantId);
  if (!payload) return false;
  return applyTreeSelectPayload(payload, setTreeSelectDepartment, isSetTreeSelectDepartment);
}

/** Navigate to cong-viec-du-an (same behavior as row name click in ActionPlanTreeTable). */
export async function navigateCongViecDuAnFromDepartment(
  params: NavigateCongViecDuAnFromDepartmentParams
): Promise<void> {
  const {
    record,
    departmentId,
    idSelect,
    tab,
    dateRange,
    profileId,
    tenantId,
    setTreeSelectDepartment,
    isSetTreeSelectDepartment = true,
    hienThiMode = CONSTANTS.ACTION_PLAN.HIEN_THI.TONG_QUAN_TOAN_CAY,
    routePrefix = ''
  } = params;
  const project = record.originalData as ProjectNode | undefined;
  if (!project) return;
  if (project?.stateApproved !== 1) {
    if (project?.nguoiTao !== profileId) {
      return;
    }
  }

  const formattedDateRange = formatDateRangeForUrl(dateRange || null);
  const idBoPhan = departmentId || idSelect || '';
  const idMucTieu = project.objectiveId || '0';

  // if (idBoPhan != project?.nguoiPhuTrachID) {
  //   hienThiMode = CONSTANTS.ACTION_PLAN.HIEN_THI.TONG_QUAN_VIEC_CUATOI;
  // }

  const raw = record.originalData as { duAnID?: string; duAnId?: string } | undefined;
  const duAnId = (raw?.duAnID ?? raw?.duAnId ?? record.id) || '';
  const prefix = routePrefix && routePrefix !== '/' ? String(routePrefix).replace(/\/$/, '') : '';
  const url = `${prefix}/cong-viec-du-an/${idBoPhan}/${idMucTieu}/${tab}/${duAnId}/${formattedDateRange}/${hienThiMode}`;



  await syncTreeSelectDepartmentFromIdBoPhan(
    idBoPhan,
    tenantId,
    setTreeSelectDepartment,
    isSetTreeSelectDepartment
  );

  sessionStorage.setItem(
    CONSTANTS.ACTION_PLAN.SESSION_KEY_QUANLY,
    JSON.stringify({
      congviecID: project.id,
    })
  );

  window.open(url, '_blank');
}
